import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Box,
  Chip,
  OutlinedInput
} from '@mui/material';

import { useAuth } from '../../../contexts/AuthContext';
import { getTenantPagingZonesAdmin, getTenantSipPagingZonesAdmin } from '../../../api/backendApi';
import { flowsApi } from '../../../services/flowsApiService';

const ACTION_TYPES = [
  { value: 'SIP_CALL', label: 'SIP Call' },
  { value: 'SIP_PAGING', label: 'SIP Paging' },
  { value: 'SEND_SMS', label: 'Send SMS' },
  { value: 'RTP_PAGING', label: 'RTP Paging' },
  { value: 'TRIGGER_FLOW', label: 'Trigger Flow' },
  { value: 'IMAGE_DISPLAY', label: 'Image Display' },
  { value: 'APPEND_EVENT', label: 'Append Event' },
  { value: 'OPEN_URL', label: 'Open URL' }
];

const ICON_OPTIONS = [
  { value: '📢', label: '📢 Speaker' },
  { value: '📱', label: '📱 Phone' },
  { value: '🚑', label: '🚑 Ambulance' },
  { value: '🔥', label: '🔥 Fire' },
  { value: '🚨', label: '🚨 Siren' },
  { value: '🚒', label: '🚒 Fire Truck' },
  { value: '⛈️', label: '⛈️ Storm' },
  { value: '🚪', label: '🚪 Door' },
  { value: '🖼️', label: '🖼️ Picture' },
  { value: '📝', label: '📝 Note' },
  { value: '🔗', label: '🔗 Link' },
  { value: '⚡', label: '⚡ Lightning' }
];

const ROLE_OPTIONS = [
  'admin',
  'superadmin',
  'tenant_admin',
  'responder',
  'overseer',
  'viewer'
];

const ButtonEditorDialog = ({ open, button, onClose, onSave }) => {
  const { tenantId } = useAuth();
  const [formData, setFormData] = useState({
    label: '',
    icon: '📢',
    actionType: 'SIP_CALL',
    endpoint: '',
    url: '',
    flowId: '',
    zoneId: '',
    zoneName: '',
    requiresConfirmation: false,
    confirmationMessage: '',
    cooldownSeconds: 30,
    visibleRoles: [],
    order: 0
  });

  const [errors, setErrors] = useState({});
  const [flowOptions, setFlowOptions] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [rtpZonesLoading, setRtpZonesLoading] = useState(false);
  const [sipZonesLoading, setSipZonesLoading] = useState(false);
  const [rtpZoneOptions, setRtpZoneOptions] = useState([]);
  const [sipZoneOptions, setSipZoneOptions] = useState([]);

  useEffect(() => {
    let active = true;

    const loadFlows = async () => {
      if (!open || !tenantId) return;
      setFlowsLoading(true);
      try {
        const resp = await flowsApi.getAll(tenantId);
        const flows = Array.isArray(resp) ? resp : (resp?.data || []);
        const opts = (Array.isArray(flows) ? flows : [])
          .map((f) => {
            const id = f?.flowId || f?.id;
            return {
              id,
              name: f?.flowName || f?.name || id,
            };
          })
          .filter((o) => !!o?.id);
        if (active) setFlowOptions(opts);
      } catch (_) {
        if (active) setFlowOptions([]);
      } finally {
        if (active) setFlowsLoading(false);
      }
    };

    loadFlows();

    return () => {
      active = false;
    };
  }, [open, tenantId]);

  useEffect(() => {
    if (button) {
      setFormData({
        label: button.label || '',
        icon: button.icon || '📢',
        actionType: button.actionType || 'SIP_CALL',
        endpoint: button.endpoint || '',
        url: button.url || '',
        flowId: button.flowId || '',
        zoneId: button.zoneId || '',
        zoneName: button.zoneName || '',
        requiresConfirmation: button.requiresConfirmation || false,
        confirmationMessage: button.confirmationMessage || '',
        cooldownSeconds: button.cooldownSeconds || 30,
        visibleRoles: button.visibleRoles || [],
        order: button.order || 0
      });
    } else {
      // Reset for new button
      setFormData({
        label: '',
        icon: '📢',
        actionType: 'SIP_CALL',
        endpoint: '',
        url: '',
        flowId: '',
        zoneId: '',
        zoneName: '',
        requiresConfirmation: false,
        confirmationMessage: '',
        cooldownSeconds: 30,
        visibleRoles: [],
        order: 0
      });
    }
    setErrors({});
  }, [button, open]);

  useEffect(() => {
    let active = true;

    const loadRtpZones = async () => {
      if (!open || !tenantId) return;
      if (!['RTP_PAGING'].includes(formData.actionType)) return;
      if (Array.isArray(rtpZoneOptions) && rtpZoneOptions.length > 0) return;

      setRtpZonesLoading(true);
      try {
        const zones = await getTenantPagingZonesAdmin(tenantId);
        const opts = (Array.isArray(zones) ? zones : [])
          .map((z) => {
            const id = z?.id || z?.zoneId;
            return {
              id,
              name: z?.name || id,
              active: z?.active,
            };
          })
          .filter((z) => !!z.id);
        if (active) setRtpZoneOptions(opts);
      } catch (_) {
        if (active) setRtpZoneOptions([]);
      } finally {
        if (active) setRtpZonesLoading(false);
      }
    };

    const loadSipZones = async () => {
      if (!open || !tenantId) return;
      if (!['SIP_PAGING'].includes(formData.actionType)) return;
      if (Array.isArray(sipZoneOptions) && sipZoneOptions.length > 0) return;

      setSipZonesLoading(true);
      try {
        const zones = await getTenantSipPagingZonesAdmin(tenantId);
        const opts = (Array.isArray(zones) ? zones : [])
          .map((z) => {
            const id = z?.id || z?.zoneId;
            return {
              id,
              name: z?.name || id,
              active: z?.active,
            };
          })
          .filter((z) => !!z.id);
        if (active) setSipZoneOptions(opts);
      } catch (_) {
        if (active) setSipZoneOptions([]);
      } finally {
        if (active) setSipZonesLoading(false);
      }
    };

    loadRtpZones();
    loadSipZones();

    return () => {
      active = false;
    };
  }, [open, tenantId, formData.actionType, rtpZoneOptions, sipZoneOptions]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.actionType) {
      newErrors.actionType = 'Action type is required';
    }

    if (formData.actionType === 'TRIGGER_FLOW' && !formData.flowId.trim()) {
      newErrors.flowId = 'Flow ID is required for TRIGGER_FLOW action';
    }

    if (['RTP_PAGING', 'SIP_PAGING'].includes(formData.actionType) && !String(formData.zoneId || '').trim()) {
      newErrors.zoneId = 'A paging zone is required';
    }

    if (['SIP_CALL', 'SEND_SMS', 'IMAGE_DISPLAY'].includes(formData.actionType) && !formData.endpoint.trim()) {
      newErrors.endpoint = 'Endpoint is required for this action type';
    }

    if (formData.actionType === 'OPEN_URL' && !String(formData.url || '').trim()) {
      newErrors.url = 'URL is required for OPEN_URL action';
    }

    if (formData.requiresConfirmation && !formData.confirmationMessage.trim()) {
      newErrors.confirmationMessage = 'Confirmation message is required when confirmation is enabled';
    }

    if (formData.cooldownSeconds < 0) {
      newErrors.cooldownSeconds = 'Cooldown must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const payload = {
      ...formData,
      cooldownSeconds: parseInt(formData.cooldownSeconds, 10)
    };

    if (payload.actionType === 'OPEN_URL') {
      payload.url = String(payload.url || payload.endpoint || '').trim();
      delete payload.endpoint;
      delete payload.zoneId;
      delete payload.zoneName;
    } else if (payload.actionType === 'TRIGGER_FLOW') {
      delete payload.endpoint;
      delete payload.url;
      delete payload.zoneId;
      delete payload.zoneName;
    } else if (['RTP_PAGING', 'SIP_PAGING'].includes(payload.actionType)) {
      delete payload.endpoint;
      delete payload.url;
      delete payload.flowId;
    } else {
      delete payload.url;
      delete payload.zoneId;
      delete payload.zoneName;
    }

    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {button ? 'Edit Action Button' : 'Add Action Button'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Label"
            value={formData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            error={!!errors.label}
            helperText={errors.label || 'Button text shown to users'}
            required
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Icon</InputLabel>
            <Select
              value={formData.icon}
              onChange={(e) => handleChange('icon', e.target.value)}
              label="Icon"
            >
              {ICON_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={formData.actionType}
              onChange={(e) => handleChange('actionType', e.target.value)}
              label="Action Type"
              error={!!errors.actionType}
            >
              {ACTION_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.actionType === 'TRIGGER_FLOW' ? (
            <FormControl fullWidth required error={!!errors.flowId} disabled={flowsLoading}>
              <InputLabel id="incident-flow-select-label">Flow</InputLabel>
              <Select
                labelId="incident-flow-select-label"
                label="Flow"
                value={formData.flowId || ''}
                onChange={(e) => handleChange('flowId', e.target.value)}
                renderValue={(val) => {
                  if (!val) return flowsLoading ? 'Loading flows…' : 'Select a flow…';
                  const found = flowOptions.find((o) => o.id === val);
                  return found ? found.name : val;
                }}
              >
                <MenuItem value="">{flowsLoading ? 'Loading flows…' : 'Select a flow…'}</MenuItem>
                {flowOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.flowId || 'Flow to trigger'}</FormHelperText>
            </FormControl>
          ) : ['RTP_PAGING', 'SIP_PAGING'].includes(formData.actionType) ? (
            <FormControl fullWidth required error={!!errors.zoneId}>
              <InputLabel id="incident-paging-zone-select-label">Paging Zone</InputLabel>
              <Select
                labelId="incident-paging-zone-select-label"
                label="Paging Zone"
                value={String(formData.zoneId || '')}
                onChange={(e) => {
                  const nextId = String(e.target.value || '');
                  const opts = formData.actionType === 'SIP_PAGING' ? sipZoneOptions : rtpZoneOptions;
                  const found = (Array.isArray(opts) ? opts : []).find((z) => String(z.id) === nextId);
                  handleChange('zoneId', nextId);
                  handleChange('zoneName', found?.name || '');
                }}
                renderValue={(val) => {
                  const selectedId = String(val || '').trim();
                  if (!selectedId) return 'Select a zone…';
                  const opts = formData.actionType === 'SIP_PAGING' ? sipZoneOptions : rtpZoneOptions;
                  const found = (Array.isArray(opts) ? opts : []).find((z) => String(z.id) === selectedId);
                  return found?.name || formData.zoneName || 'Paging zone';
                }}
              >
                <MenuItem value="">Select a zone…</MenuItem>
                {(formData.actionType === 'SIP_PAGING' ? sipZoneOptions : rtpZoneOptions).map((z) => (
                  <MenuItem key={z.id} value={z.id}>
                    {z.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.zoneId ||
                  (formData.actionType === 'SIP_PAGING'
                    ? (sipZonesLoading ? 'Loading SIP paging zones…' : 'SIP paging zones configured in Admin > Paging > SIP Paging')
                    : (rtpZonesLoading ? 'Loading RTP paging zones…' : 'RTP paging zones configured in Admin > Paging > RTP Paging'))}
              </FormHelperText>

              {((formData.actionType === 'SIP_PAGING' && !sipZonesLoading && (!Array.isArray(sipZoneOptions) || sipZoneOptions.length === 0)) ||
                (formData.actionType === 'RTP_PAGING' && !rtpZonesLoading && (!Array.isArray(rtpZoneOptions) || rtpZoneOptions.length === 0))) && (
                <FormHelperText error={!!errors.zoneId}>
                  No paging zones found for this tenant.
                </FormHelperText>
              )}
            </FormControl>
          ) : (
            <TextField
              label={formData.actionType === 'OPEN_URL' ? 'URL' : 'Endpoint'}
              value={formData.actionType === 'OPEN_URL' ? formData.url : formData.endpoint}
              onChange={(e) =>
                handleChange(formData.actionType === 'OPEN_URL' ? 'url' : 'endpoint', e.target.value)
              }
              error={formData.actionType === 'OPEN_URL' ? !!errors.url : !!errors.endpoint}
              helperText={
                (formData.actionType === 'OPEN_URL'
                  ? errors.url || 'URL to open (e.g., https://example.com)'
                  : errors.endpoint || 'API endpoint path (e.g., /sip-paging/authorize)')
              }
              required={['SIP_CALL', 'SEND_SMS', 'IMAGE_DISPLAY', 'OPEN_URL'].includes(formData.actionType)}
              fullWidth
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.requiresConfirmation}
                onChange={(e) => handleChange('requiresConfirmation', e.target.checked)}
              />
            }
            label="Requires Confirmation"
          />

          {formData.requiresConfirmation && (
            <TextField
              label="Confirmation Message"
              value={formData.confirmationMessage}
              onChange={(e) => handleChange('confirmationMessage', e.target.value)}
              error={!!errors.confirmationMessage}
              helperText={errors.confirmationMessage || 'Message shown in confirmation dialog'}
              required
              multiline
              rows={2}
              fullWidth
            />
          )}

          <TextField
            label="Cooldown (seconds)"
            type="number"
            value={formData.cooldownSeconds}
            onChange={(e) => handleChange('cooldownSeconds', e.target.value)}
            error={!!errors.cooldownSeconds}
            helperText={errors.cooldownSeconds || 'Time before button can be clicked again'}
            inputProps={{ min: 0, max: 300 }}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Visible to Roles</InputLabel>
            <Select
              multiple
              value={formData.visibleRoles}
              onChange={(e) => handleChange('visibleRoles', e.target.value)}
              input={<OutlinedInput label="Visible to Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {ROLE_OPTIONS.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Order"
            type="number"
            value={formData.order}
            onChange={(e) => handleChange('order', e.target.value)}
            helperText="Display order (lower numbers appear first)"
            inputProps={{ min: 0 }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ButtonEditorDialog;
